// src/components/laboratory/tabs/ContractsTab.jsx
import React, { useState } from 'react';
import { Plus, FileText, Calendar, Search, PencilLine, Trash2, Download } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const ContractsTab = ({ 
  contracts, 
  loading, 
  onAddContract, 
  onEditContract, 
  onDeleteContract 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Determine if contract is active
  const isContractActive = (contract) => {
    const now = new Date();
    const endDate = new Date(contract.dateEnd);
    return now <= endDate;
  };

  // Filter contracts based on search term and status
  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = 
      (contract.number && contract.number.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (contract.nom && contract.nom.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const active = isContractActive(contract);
    const matchesStatus = 
      statusFilter === 'all' || 
      (statusFilter === 'active' && active) || 
      (statusFilter === 'expired' && !active);
    
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Contrats ({contracts.length})</h3>
        <button
          onClick={onAddContract}
          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-500"
        >
          <Plus className="-ml-0.5 mr-1 h-4 w-4" />
          Nouveau contrat
        </button>
      </div>

      {/* Search and filter bar */}
      <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-3">
        <div className="relative w-full">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm"
            placeholder="Rechercher un contrat..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="relative w-full sm:w-40">
          <select
            className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Tous les statuts</option>
            <option value="active">Actifs</option>
            <option value="expired">Expirés</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="py-10 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-green-700 border-r-transparent"></div>
          <p className="mt-2 text-gray-500">Chargement des contrats...</p>
        </div>
      ) : filteredContracts.length === 0 ? (
        <div className="text-center py-8 bg-white border border-gray-200 rounded-lg">
          <FileText size={40} className="mx-auto text-gray-400 mb-2" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun contrat</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || statusFilter !== 'all' ? 
              "Aucun contrat ne correspond à vos critères." : 
              "Ce laboratoire n'a pas encore de contrats."}
          </p>
          {(searchTerm || statusFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
              }}
              className="mt-3 text-sm text-green-600 hover:text-green-500"
            >
              Réinitialiser les filtres
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {filteredContracts.map((contract) => {
              const isActive = isContractActive(contract);
              const startDate = new Date(contract.dateStart);
              const endDate = new Date(contract.dateEnd);
              
              return (
                <li key={contract.id}>
                  <div className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <div className="mb-2 sm:mb-0">
                        <p className="text-sm font-medium text-indigo-600">
                        {contract.nom || `Contrat ${contract.number}`}
                        </p>
                        <div className="mt-1 flex flex-col sm:flex-row sm:items-center">
                          <div className="flex items-center mr-4">
                            <Calendar className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            <p className="text-sm text-gray-500">
                              {format(startDate, 'dd/MM/yyyy', { locale: fr })} - {format(endDate, 'dd/MM/yyyy', { locale: fr })}
                            </p>
                          </div>
                          <p className="text-sm text-gray-500 mt-1 sm:mt-0">
                            Numéro: {contract.number}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-4">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {isActive ? 'Actif' : 'Expiré'}
                        </span>
                        
                        {contract.piecesJointes && (
                          <a
                            href={`${process.env.REACT_APP_API_URL?.replace('/graphql', '')}/api/contracts/download/${contract.piecesJointes}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            className="text-blue-600 hover:text-blue-900"
                            title="Télécharger le contrat"
                          >
                            <Download size={18} />
                          </a>
                        )}
                        
                        <div className="flex space-x-2">
                          <button
                            onClick={() => onEditContract(contract)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Modifier"
                          >
                            <PencilLine size={18} />
                          </button>
                          <button
                            onClick={() => onDeleteContract(contract)}
                            className="text-red-600 hover:text-red-900"
                            title="Supprimer"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ContractsTab;